import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  Calendar,
  Check,
  CheckCircle2,
  ChevronRight,
  FileText,
  Lock,
  Mail,
  MessageCircle,
} from "lucide-react";
import DiyLayout from "../../components/Layout/DiyLayout";
import { billingApi } from "../../api/billingApi";
import { useAuth } from "../../hooks/useAuth";
import { useDiySelfClient } from "../../hooks/useDiySelfClient";
import { documentsApi } from "../../api/documentsApi";
import type { ClientDocument } from "../../api/documentsApi";
import { disputesApi } from "../../api/disputesApi";
import { disputeLettersApi } from "../../api/disputeLettersApi";
import { aiApi } from "../../api/aiApi";
import { scoresApi } from "../../api/scoresApi";
import type { DisputeItem, DisputeLetter } from "../../types";
import type { CreditAnalysis } from "../../types";
import { CardGridSkeleton, Skeleton } from "../../components/ui/Skeleton";
import { normalizeDiyDisputeBucket } from "./diyShared";
import { ScrollReveal } from "../../components/diy/ScrollReveal";
import { AnimatedCounter } from "../../components/diy/AnimatedCounter";
import { ScoreGauge } from "../../components/diy/ScoreGauge";
import "../../styles/diy-animations.css";

type WizardStatus = "completed" | "in_progress" | "up_next" | "locked";

function isCreditReportDoc(d: ClientDocument): boolean {
  return d.category === "CreditReport" || d.category === "credit_report";
}

function earliestSentMs(disputes: DisputeItem[], letters: DisputeLetter[]): number | null {
  const times: number[] = [];
  for (const l of letters) {
    if (l.sentAt) {
      const t = new Date(l.sentAt).getTime();
      if (!Number.isNaN(t)) {
        times.push(t);
      }
    }
  }
  for (const d of disputes) {
    if (d.lastLetterSentAt) {
      const t = new Date(d.lastLetterSentAt).getTime();
      if (!Number.isNaN(t)) {
        times.push(t);
      }
    }
  }
  if (times.length === 0) {
    return null;
  }
  return Math.min(...times);
}

function gaugePropsForScore(score: number): { score: number; label: string; color: string; glowColor: string } {
  if (!score || score < 300) {
    return { score: 520, label: "ADD", color: "#64748B", glowColor: "rgba(100,116,139,0.25)" };
  }
  if (score >= 750) {
    return { score, label: "EXCELLENT", color: "#10B981", glowColor: "rgba(16,185,129,0.35)" };
  }
  if (score >= 700) {
    return { score, label: "GOOD", color: "#34D399", glowColor: "rgba(52,211,153,0.35)" };
  }
  if (score >= 580) {
    return { score, label: "FAIR", color: "#F59E0B", glowColor: "rgba(245,158,11,0.35)" };
  }
  return { score, label: "FOCUS", color: "#EF4444", glowColor: "rgba(239,68,68,0.35)" };
}

const DiyDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { client, clientId, loading: clientLoading, error: clientError, reload: reloadClient } = useDiySelfClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);

  const [docs, setDocs] = useState<ClientDocument[]>([]);
  const [disputes, setDisputes] = useState<DisputeItem[]>([]);
  const [letters, setLetters] = useState<DisputeLetter[]>([]);
  const [analysis, setAnalysis] = useState<CreditAnalysis | null>(null);
  const [scoreHistory, setScoreHistory] = useState<{ period: string; bureau: string; score: number }[]>([]);
  const [summaryRow, setSummaryRow] = useState<{
    deltaEquifax: number | null;
    deltaExperian: number | null;
    deltaTransUnion: number | null;
  } | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState("");

  const firstName = user?.user?.firstName ?? "there";

  const checkoutFlag = searchParams.get("checkout");
  const sessionIdParam = searchParams.get("session_id");

  useEffect(() => {
    if (checkoutFlag !== "success" || !sessionIdParam) {
      return;
    }
    let cancelled = false;
    billingApi
      .verifyCheckout(sessionIdParam)
      .then(() => {
        if (!cancelled) {
          setVerifyMsg("Subscription confirmed. Welcome!");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setVerifyMsg("We could not confirm checkout yet. If you completed payment, wait a moment and refresh.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSearchParams(
            (prev) => {
              const p = new URLSearchParams(prev);
              p.delete("session_id");
              p.delete("checkout");
              return p;
            },
            { replace: true }
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [checkoutFlag, sessionIdParam, setSearchParams]);

  const loadDashboardData = useCallback(async () => {
    if (!clientId) {
      setDocs([]);
      setDisputes([]);
      setLetters([]);
      setAnalysis(null);
      setScoreHistory([]);
      setSummaryRow(null);
      return;
    }
    setDataLoading(true);
    setDataError("");
    try {
      const [docList, disputeList, analysisResult, summaries, hist] = await Promise.all([
        documentsApi.list(clientId),
        disputesApi.getAllForClient(clientId),
        aiApi.getLatestAnalysis(clientId).catch(() => null),
        scoresApi.getSummary().catch(() => []),
        scoresApi.getHistory(clientId, 365 * 3).catch(() => []),
      ]);
      setDocs(docList);
      setDisputes(disputeList);
      setAnalysis(analysisResult);
      setScoreHistory(hist);
      const row = summaries.find((s) => s.clientId === clientId);
      setSummaryRow(
        row
          ? {
              deltaEquifax: row.deltaEquifax,
              deltaExperian: row.deltaExperian,
              deltaTransUnion: row.deltaTransUnion,
            }
          : null
      );
      const letterLists = await Promise.all(
        disputeList.map((d) => disputeLettersApi.getLettersForDispute(d.id).catch(() => [] as DisputeLetter[]))
      );
      setLetters(letterLists.flat());
    } catch {
      setDataError("Could not load dashboard data.");
      setDocs([]);
      setDisputes([]);
      setLetters([]);
      setAnalysis(null);
      setScoreHistory([]);
    } finally {
      setDataLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void loadDashboardData();
  }, [loadDashboardData]);

  const creditReportDocs = useMemo(() => docs.filter(isCreditReportDoc), [docs]);
  const step1Done = creditReportDocs.length > 0;
  const step2Done = disputes.length > 0;
  const step3Done = analysis !== null;
  const step4Done = letters.length > 0;
  const step5Done = earliestSentMs(disputes, letters) !== null;
  const secondReportOrScoreChange = useMemo(() => {
    if (creditReportDocs.length >= 2) {
      return true;
    }
    if (scoreHistory.length < 2) {
      return false;
    }
    const scores = scoreHistory.map((p) => p.score).filter((s) => s > 0);
    if (scores.length < 2) {
      return false;
    }
    return Math.max(...scores) !== Math.min(...scores);
  }, [creditReportDocs.length, scoreHistory]);
  const step6Done = step5Done && secondReportOrScoreChange;

  const resolvedCount = useMemo(
    () => disputes.filter((d) => normalizeDiyDisputeBucket(d.status) === "resolved").length,
    [disputes]
  );

  const lettersSentCount = useMemo(() => letters.filter((l) => Boolean(l.sentAt)).length, [letters]);

  const firstSentMs = useMemo(() => earliestSentMs(disputes, letters), [disputes, letters]);
  const daysSinceFirstLetter = useMemo(() => {
    if (firstSentMs === null) {
      return 0;
    }
    return Math.max(0, Math.floor((Date.now() - firstSentMs) / 86400000));
  }, [firstSentMs]);

  const firstSentLabel = useMemo(() => {
    if (firstSentMs === null) {
      return "";
    }
    return new Date(firstSentMs).toLocaleDateString();
  }, [firstSentMs]);

  const wizardSteps = useMemo(() => {
    const s1 = step1Done;
    const s2 = step2Done;
    const s3 = step3Done;
    const s4 = step4Done;
    const s5 = step5Done;

    const st = (done: boolean, current: boolean, locked: boolean): WizardStatus => {
      if (locked) {
        return "locked";
      }
      if (done) {
        return "completed";
      }
      if (current) {
        return "in_progress";
      }
      return "up_next";
    };

    return [
      {
        n: 1,
        title: "Upload Credit Report",
        desc: "Upload your credit report PDF.",
        status: st(s1, !s1, false),
        done: s1,
        path: "/diy/documents" as const,
      },
      {
        n: 2,
        title: "AI Analysis",
        desc: "Let AI scan for errors and disputable items.",
        status: st(s2, s1 && !s2, !s1),
        done: s2,
        path: "/diy/documents" as const,
      },
      {
        n: 3,
        title: "Review Findings",
        desc: "Review what AI found on your report.",
        status: st(s3, s2 && !s3, !s2),
        done: s3,
        path: "/diy/my-report" as const,
      },
      {
        n: 4,
        title: "Create Disputes",
        desc: "Select items to dispute.",
        status: st(s4, s3 && !s4, !s3),
        done: s4,
        path: "/diy/my-disputes" as const,
      },
      {
        n: 5,
        title: "Generate Letters",
        desc: "AI creates your dispute letters.",
        status: st(s5, s4 && !s5, !s4),
        done: s5,
        path: "/diy/my-letters" as const,
      },
      {
        n: 6,
        title: "Send & Track",
        desc: "Mail your letters and track responses.",
        status: st(step6Done, s5 && !step6Done, !s5),
        done: step6Done,
        path: "/diy/my-letters" as const,
      },
    ];
  }, [step1Done, step2Done, step3Done, step4Done, step5Done, step6Done]);

  const completedSteps = wizardSteps.filter((s) => s.done).length;
  const progressPct = (completedSteps / 6) * 100;

  const badgeLabel = (status: WizardStatus) => {
    switch (status) {
      case "completed":
        return "Done";
      case "in_progress":
        return "In Progress";
      case "up_next":
        return "Up Next";
      default:
        return "Locked";
    }
  };

  const loading = clientLoading || (Boolean(clientId) && dataLoading);

  const avgDelta = useMemo(() => {
    const parts = [summaryRow?.deltaEquifax, summaryRow?.deltaExperian, summaryRow?.deltaTransUnion].filter(
      (x): x is number => typeof x === "number" && !Number.isNaN(x)
    );
    if (parts.length === 0) {
      return 0;
    }
    return Math.round(parts.reduce((a, b) => a + b, 0) / parts.length);
  }, [summaryRow]);

  const hasScores =
    client &&
    [client.equifaxScore, client.experianScore, client.transUnionScore].some((s) => typeof s === "number" && s > 0);

  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <DiyLayout>
      <div className="mx-auto max-w-lg md:max-w-2xl">
        {verifyMsg ? (
          <div
            className="glass-card mb-6 rounded-2xl px-4 py-3 text-sm"
            style={{ color: "#E0F2FE", fontFamily: "'DM Sans', sans-serif" }}
          >
            {verifyMsg}
          </div>
        ) : null}

        <div className="animate-fade-up mb-8">
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 24, color: "#F1F5F9" }}>
            Welcome back, {firstName}
          </h1>
          <p className="mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#94A3B8" }}>
            Here&apos;s where you stand · {todayLabel}
          </p>
        </div>

        {clientError ? (
          <div
            className="mb-6 rounded-2xl border px-4 py-3 text-sm"
            style={{
              borderColor: "rgba(239,68,68,0.25)",
              background: "rgba(239,68,68,0.08)",
              color: "#FECACA",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {clientError}
            <button
              type="button"
              onClick={() => void reloadClient()}
              className="ml-3 rounded-lg border-0 px-3 py-1 text-xs"
              style={{ background: "rgba(239,68,68,0.2)", color: "#FECACA" }}
            >
              Retry
            </button>
          </div>
        ) : null}

        {loading && !client ? (
          <CardGridSkeleton cards={3} />
        ) : client ? (
          <>
            <ScrollReveal>
              <div
                className="glass-card rounded-2xl p-6"
                style={{
                  background: "rgba(16, 185, 129, 0.06)",
                  border: "1px solid rgba(16, 185, 129, 0.15)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                }}
              >
                <p
                  className="mb-4"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: 10,
                    color: "#10B981",
                    letterSpacing: 2,
                  }}
                >
                  YOUR CREDIT SCORES
                </p>
                {loading ? (
                  <div className="grid grid-cols-3 gap-2">
                    <Skeleton className="h-32 rounded-xl" />
                    <Skeleton className="h-32 rounded-xl" />
                    <Skeleton className="h-32 rounded-xl" />
                  </div>
                ) : hasScores ? (
                  <>
                    <div className="flex flex-wrap justify-around gap-4">
                      {(
                        [
                          ["Equifax", client.equifaxScore],
                          ["Experian", client.experianScore],
                          ["TransUnion", client.transUnionScore],
                        ] as const
                      ).map(([label, raw]) => {
                        const g = gaugePropsForScore(raw ?? 0);
                        return (
                          <div key={label} className="flex flex-col items-center">
                            <span
                              className="mb-1"
                              style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontWeight: 600,
                                fontSize: 9,
                                color: "#94A3B8",
                                letterSpacing: 2,
                              }}
                            >
                              {label === "Equifax" ? "EQ" : label === "Experian" ? "EX" : "TU"}
                            </span>
                            {raw && raw > 0 ? (
                              <ScoreGauge
                                score={raw}
                                label={g.label}
                                color={g.color}
                                glowColor={g.glowColor}
                                animated
                              />
                            ) : (
                              <div
                                className="flex h-[100px] w-[120px] items-center justify-center rounded-xl"
                                style={{
                                  fontFamily: "'Sora', sans-serif",
                                  fontSize: 28,
                                  fontWeight: 800,
                                  color: "#64748B",
                                }}
                              >
                                —
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div
                      className="mt-4 flex items-center justify-center gap-2 text-center"
                      style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 500 }}
                    >
                      {avgDelta > 0 ? (
                        <ArrowUp className="h-4 w-4" style={{ color: "#10B981" }} aria-hidden />
                      ) : avgDelta < 0 ? (
                        <ArrowDown className="h-4 w-4" style={{ color: "#EF4444" }} aria-hidden />
                      ) : null}
                      <span
                        style={{
                          color: avgDelta > 0 ? "#10B981" : avgDelta < 0 ? "#EF4444" : "#94A3B8",
                        }}
                      >
                        {avgDelta > 0 ? "+" : ""}
                        {avgDelta} pts since tracking started
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="py-4 text-center">
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#94A3B8" }}>
                      Add your credit scores to start tracking progress.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate("/diy/documents")}
                      className="mt-4 cursor-pointer rounded-xl border-0 px-6 py-3 text-white transition-transform hover:scale-[1.02]"
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 600,
                        background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                        boxShadow: "0 4px 15px rgba(16,185,129,0.3)",
                      }}
                    >
                      Add scores
                    </button>
                  </div>
                )}
              </div>
            </ScrollReveal>

            {dataError ? (
              <div
                className="mt-6 rounded-2xl border px-4 py-3 text-sm"
                style={{
                  borderColor: "rgba(245,158,11,0.3)",
                  background: "rgba(120,53,15,0.2)",
                  color: "#FDE68A",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {dataError}
                <button
                  type="button"
                  onClick={() => void loadDashboardData()}
                  className="ml-3 rounded-lg border-0 px-3 py-1 text-xs"
                  style={{ background: "rgba(245,158,11,0.2)", color: "#FDE68A" }}
                >
                  Retry
                </button>
              </div>
            ) : null}

            <section className="mt-8">
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: 10,
                  color: "#10B981",
                  letterSpacing: 2,
                  marginBottom: 16,
                }}
              >
                YOUR JOURNEY
              </p>
              <div className="h-2 w-full overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.06)" }}>
                <div
                  className="h-2 rounded-full"
                  style={{
                    width: `${progressPct}%`,
                    background: "linear-gradient(90deg, #10B981, #0EA5E9)",
                    transition: "width 1s ease",
                  }}
                />
              </div>
              <p className="mb-6 mt-2" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: 12, color: "#94A3B8" }}>
                {completedSteps} of 6 steps completed
              </p>

              <div className="flex flex-col gap-3">
                {wizardSteps.map((step, idx) => {
                  const clickable = step.status === "in_progress" || step.status === "up_next";
                  const inner = (
                    <div className="flex gap-4">
                      <div className="shrink-0">
                        {step.status === "completed" ? (
                          <div
                            className="flex h-11 w-11 items-center justify-center rounded-xl border"
                            style={{
                              background: "rgba(16,185,129,0.15)",
                              borderColor: "rgba(16,185,129,0.3)",
                            }}
                          >
                            <Check className="h-5 w-5" strokeWidth={2.5} style={{ color: "#10B981" }} />
                          </div>
                        ) : step.status === "in_progress" ? (
                          <div
                            className="relative flex h-11 w-11 items-center justify-center rounded-xl border"
                            style={{
                              background: "rgba(14,165,233,0.15)",
                              borderColor: "rgba(14,165,233,0.3)",
                              boxShadow: "0 0 12px rgba(14,165,233,0.25)",
                            }}
                          >
                            <span className="h-2 w-2 animate-pulse rounded-full" style={{ background: "#0EA5E9" }} />
                          </div>
                        ) : step.status === "locked" ? (
                          <div
                            className="flex h-11 w-11 items-center justify-center rounded-xl border opacity-50"
                            style={{
                              background: "rgba(255,255,255,0.04)",
                              borderColor: "rgba(255,255,255,0.1)",
                            }}
                          >
                            <Lock className="h-5 w-5" style={{ color: "#64748B" }} />
                          </div>
                        ) : (
                          <div
                            className="flex h-11 w-11 items-center justify-center rounded-xl border"
                            style={{
                              background: "rgba(255,255,255,0.04)",
                              borderColor: "rgba(255,255,255,0.1)",
                              fontFamily: "'Sora', sans-serif",
                              fontWeight: 700,
                              fontSize: 14,
                              color: "#64748B",
                            }}
                          >
                            {step.n}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3
                          style={{
                            fontFamily: "'Sora', sans-serif",
                            fontWeight: 600,
                            fontSize: 15,
                            color: step.status === "locked" ? "#64748B" : "#E2E8F0",
                          }}
                        >
                          {step.title}
                        </h3>
                        <p
                          className="mt-1"
                          style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 12,
                            color: step.status === "locked" ? "#475569" : "#94A3B8",
                          }}
                        >
                          {step.desc}
                        </p>

                        {step.n === 1 && (
                          <div className="mt-3">
                            {!step1Done ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/diy/documents");
                                }}
                                className="cursor-pointer rounded-xl border-0 px-4 py-2 text-sm text-white"
                                style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontWeight: 600,
                                  background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                                }}
                              >
                                Upload Credit Report
                              </button>
                            ) : (
                              <div className="flex flex-wrap items-center gap-3 text-sm" style={{ color: "#10B981" }}>
                                <span>✓ Report uploaded</span>
                                <button
                                  type="button"
                                  className="cursor-pointer border-0 bg-transparent underline"
                                  style={{ color: "#0EA5E9", fontFamily: "'DM Sans', sans-serif" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate("/diy/documents");
                                  }}
                                >
                                  Upload another
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {step.n === 2 && (
                          <div className="mt-3">
                            {step.status === "locked" ? (
                              <p style={{ fontSize: 12, color: "#475569", fontFamily: "'DM Sans', sans-serif" }}>Complete step 1 first.</p>
                            ) : !step2Done ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/diy/documents");
                                }}
                                className="cursor-pointer rounded-xl border-0 px-4 py-2 text-sm text-white"
                                style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontWeight: 600,
                                  background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                                }}
                              >
                                Parse Your Report
                              </button>
                            ) : (
                              <p style={{ fontSize: 12, color: "#10B981", fontFamily: "'DM Sans', sans-serif" }}>
                                ✓ {disputes.length} negative item{disputes.length === 1 ? "" : "s"} found
                              </p>
                            )}
                          </div>
                        )}

                        {step.n === 3 && (
                          <div className="mt-3">
                            {step.status === "locked" ? (
                              <p style={{ fontSize: 12, color: "#475569", fontFamily: "'DM Sans', sans-serif" }}>Complete step 2 first.</p>
                            ) : step3Done ? (
                              <p style={{ fontSize: 12, color: "#10B981", fontFamily: "'DM Sans', sans-serif" }}>
                                ✓ AI analysis on file — open disputes anytime to refine your plan.
                              </p>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/diy/my-report");
                                }}
                                className="cursor-pointer rounded-xl border-0 px-4 py-2 text-sm text-white"
                                style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontWeight: 600,
                                  background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                                }}
                              >
                                Review findings
                              </button>
                            )}
                          </div>
                        )}

                        {step.n === 4 && (
                          <div className="mt-3">
                            {step.status === "locked" ? (
                              <p style={{ fontSize: 12, color: "#475569", fontFamily: "'DM Sans', sans-serif" }}>Complete step 3 first.</p>
                            ) : step4Done ? (
                              <p style={{ fontSize: 12, color: "#10B981", fontFamily: "'DM Sans', sans-serif" }}>
                                ✓ {letters.length} letter{letters.length === 1 ? "" : "s"} generated
                              </p>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/diy/my-letters");
                                }}
                                className="cursor-pointer rounded-xl border-0 px-4 py-2 text-sm text-white"
                                style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontWeight: 600,
                                  background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                                }}
                              >
                                Generate Letters
                              </button>
                            )}
                          </div>
                        )}

                        {step.n === 5 && (
                          <div className="mt-3">
                            {step.status === "locked" ? (
                              <p style={{ fontSize: 12, color: "#475569", fontFamily: "'DM Sans', sans-serif" }}>Complete step 4 first.</p>
                            ) : step5Done ? (
                              <div style={{ fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}>
                                <p style={{ color: "#10B981" }}>✓ Letters sent on {firstSentLabel}</p>
                                <p className="mt-1" style={{ color: "#FBBF24" }}>
                                  Waiting for responses (Day {Math.min(daysSinceFirstLetter, 45)} of 30-45)
                                </p>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/diy/my-letters");
                                }}
                                className="cursor-pointer rounded-xl border-0 px-4 py-2 text-sm text-white"
                                style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontWeight: 600,
                                  background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                                }}
                              >
                                Track My Letters
                              </button>
                            )}
                          </div>
                        )}

                        {step.n === 6 && (
                          <div className="mt-3">
                            {step.status === "locked" ? (
                              <p style={{ fontSize: 12, color: "#475569", fontFamily: "'DM Sans', sans-serif" }}>Complete step 5 first.</p>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/diy/my-report");
                                }}
                                className="cursor-pointer rounded-xl border-0 px-4 py-2 text-sm text-white"
                                style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontWeight: 600,
                                  background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                                }}
                              >
                                Check Results
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-2">
                        <span
                          className={`rounded-full px-3 py-1 ${step.status === "in_progress" ? "animate-pulse" : ""}`}
                          style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontWeight: 600,
                            fontSize: 10,
                            ...(step.status === "completed"
                              ? { background: "rgba(16,185,129,0.1)", color: "#10B981" }
                              : step.status === "in_progress"
                                ? { background: "rgba(14,165,233,0.1)", color: "#0EA5E9" }
                                : step.status === "up_next"
                                  ? { background: "rgba(245,158,11,0.1)", color: "#F59E0B" }
                                  : { background: "rgba(255,255,255,0.04)", color: "#64748B" }),
                          }}
                        >
                          {badgeLabel(step.status)}
                          {step.status === "locked" ? <Lock className="ml-1 inline h-3 w-3" /> : null}
                        </span>
                      </div>
                    </div>
                  );

                  return (
                    <ScrollReveal key={step.n} delay={idx * 0.06}>
                      {clickable ? (
                        <button
                          type="button"
                          onClick={() => navigate(step.path)}
                          className="glass-card-subtle card-hover w-full cursor-pointer rounded-2xl border-0 p-5 text-left"
                          style={{
                            background: "rgba(255, 255, 255, 0.03)",
                            border: "1px solid rgba(255, 255, 255, 0.07)",
                          }}
                        >
                          {inner}
                        </button>
                      ) : (
                        <div
                          className="glass-card-subtle rounded-2xl p-5"
                          style={{
                            background: "rgba(255, 255, 255, 0.03)",
                            border: "1px solid rgba(255, 255, 255, 0.07)",
                          }}
                        >
                          {inner}
                        </div>
                      )}
                    </ScrollReveal>
                  );
                })}
              </div>
            </section>

            <ScrollReveal>
              <div className="mt-8 grid grid-cols-2 gap-3">
                {(
                  [
                    {
                      label: "Disputes",
                      end: disputes.length,
                      Icon: FileText,
                      color: "#0EA5E9",
                    },
                    {
                      label: "Letters Sent",
                      end: lettersSentCount,
                      Icon: Mail,
                      color: "#10B981",
                    },
                    {
                      label: "Resolved",
                      end: resolvedCount,
                      Icon: CheckCircle2,
                      color: "#8B5CF6",
                    },
                    {
                      label: "Days Active",
                      end: daysSinceFirstLetter,
                      Icon: Calendar,
                      color: "#F59E0B",
                    },
                  ] as const
                ).map((s) => (
                  <div
                    key={s.label}
                    className="glass-card-subtle rounded-xl p-4"
                    style={{
                      background: "rgba(255, 255, 255, 0.03)",
                      border: "1px solid rgba(255, 255, 255, 0.07)",
                    }}
                  >
                    <div
                      className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg"
                      style={{ background: `${s.color}1F` }}
                    >
                      <s.Icon className="h-[18px] w-[18px]" strokeWidth={2} color={s.color} />
                    </div>
                    <p style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 24, color: "#F1F5F9" }}>
                      <AnimatedCounter end={s.end} duration={1200} />
                    </p>
                    <p
                      className="mt-1"
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 500,
                        fontSize: 11,
                        color: "#94A3B8",
                        letterSpacing: 0.5,
                      }}
                    >
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <button
                type="button"
                onClick={() => navigate("/diy/ai-coach")}
                className="card-hover mt-6 flex w-full cursor-pointer items-center gap-4 rounded-2xl border-0 p-5 text-left"
                style={{
                  background: "rgba(139,92,246,0.06)",
                  border: "1px solid rgba(139,92,246,0.15)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                }}
              >
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
                  style={{ background: "rgba(139,92,246,0.15)" }}
                >
                  <MessageCircle className="h-6 w-6" strokeWidth={2} color="#8B5CF6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 15, color: "#E2E8F0" }}>AI Credit Coach</p>
                  <p className="mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#94A3B8" }}>
                    Ask anything about your credit journey
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0" color="#8B5CF6" />
              </button>
            </ScrollReveal>
          </>
        ) : null}
      </div>
    </DiyLayout>
  );
};

export default DiyDashboardPage;
